import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  NavLink,
  useParams,
} from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';

import { useFormat } from '../../components/FormatContext';

import AddShopModal from './components/AddShopModal';
import ComposerRichText from './components/ComposerRichText';
import MessageBubble from './components/MessageBubble';
import MotoSearchModal from './components/MotoSearchModal';

import useRequestDetail from './hooks/useRequestDetail';

import {
  statusColorMap,
} from './utils/requestConstants';
import {
  prettyEnum,
} from './utils/requestHelpers';
import requestService from './services/requestService';
import {
  buildMotoQuoteHtml,
} from './utils/buildMotoQuoteHtml';

import useCompanyContext from '../../hooks/useCompanyContext';

const RequestForm = () => {
  const { id } =
    useParams();

  const {
    baseFormatters,
  } = useFormat();

  const {
    isAdmin,
    buildCompanyUrl,
  } = useCompanyContext();

  const [
    composerInitialHtml,
    setComposerInitialHtml,
  ] = useState('');

  const [
    quoteLoading,
    setQuoteLoading,
  ] = useState(false);

  const [
    showAddShop,
    setShowAddShop,
  ] = useState(false);

  const [
    showMotoSearch,
    setShowMotoSearch,
  ] = useState(false);

  const listRef =
    useRef(null);

  const {
    loading,
    errorMsg,
    request,
    messages,
    msgLoading,
    msgError,
    shops,
    participantsLoading,
    participantsError,
    existingShopIds,
    fetchRequest,
    fetchMessages,
    fetchParticipants,
    handleChangeStatus,
    handleSend,
    addShops,
    removeShop,
  } = useRequestDetail(id);

  const scrollMessagesToBottom =
    useCallback(() => {
      requestAnimationFrame(
        () => {
          const element =
            listRef.current;

          if (element) {
            element.scrollTop =
              element.scrollHeight;
          }
        },
      );
    }, []);

  useEffect(() => {
    if (!id) {
      return;
    }

    fetchRequest();
    fetchParticipants();

    fetchMessages().then(
      scrollMessagesToBottom,
    );
  }, [
    id,
    fetchMessages,
    fetchParticipants,
    fetchRequest,
    scrollMessagesToBottom,
  ]);

  const refreshMessagesToBottom =
    useCallback(async () => {
      await fetchMessages();

      scrollMessagesToBottom();
    }, [
      fetchMessages,
      scrollMessagesToBottom,
    ]);

  const handleStatusChange =
    useCallback(
      async (nextStatus) => {
        try {
          await handleChangeStatus(
            nextStatus,
          );
        } catch (error) {
          alert(
            error?.response?.data
              ?.message ||
            error?.message ||
            'Update status failed',
          );

          await fetchRequest();
        }
      },
      [
        fetchRequest,
        handleChangeStatus,
      ],
    );

  const handleRemoveShop =
    useCallback(
      async (
        participantIdOrShopId,
      ) => {
        const target =
          (shops || []).find(
            (shop) =>
              shop.participantId ===
              participantIdOrShopId ||
              shop.shopId ===
              participantIdOrShopId,
          );

        const participantId =
          target?.participantId;

        if (!participantId) {
          alert(
            'Missing participantId for removal.',
          );

          return;
        }

        try {
          await removeShop(
            participantId,
          );
        } catch (error) {
          alert(
            error?.response?.data
              ?.message ||
            error?.message ||
            'Remove shop failed',
          );
        }
      },
      [
        removeShop,
        shops,
      ],
    );

  const handleGenerateMotoQuote =
    useCallback(
      async (payload) => {
        try {
          setQuoteLoading(
            true,
          );

          const result =
            await requestService.generateMotoQuote(
              id,
              payload,
            );

          const quote =
            result?.quote;

          if (!quote) {
            alert(
              'Moto quote generated, but no quote data was returned.',
            );

            return;
          }

          const customerName =
            `${request.contactFirstName || ''} ${request.contactLastName || ''}`.trim() ||
            'Customer';

          const initialHtml =
            buildMotoQuoteHtml(
              quote,
              {
                customerName,
              },
            );

          setComposerInitialHtml(
            initialHtml,
          );

          setShowMotoSearch(
            false,
          );

          requestAnimationFrame(
            () => {
              const composerElement =
                document.getElementById(
                  'request-composer',
                );

              composerElement?.scrollIntoView(
                {
                  behavior:
                    'smooth',
                  block:
                    'center',
                },
              );
            },
          );
        } catch (error) {
          alert(
            error?.response?.data
              ?.message ||
            error?.message ||
            'Generate moto quote failed',
          );
        } finally {
          setQuoteLoading(
            false,
          );
        }
      },
      [
        id,
        request.contactFirstName,
        request.contactLastName,
      ],
    );

  const statusColor =
    statusColorMap[
    request.status
    ] || {};

  const customerName =
    `${request.contactFirstName || ''} ${request.contactLastName || ''}`.trim() ||
    'Customer';

  return (
    <Container
      fluid
      className="d-flex flex-column"
    >
      <Row className="mb-3">
        <Col sm={8}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-semibold">
              Request Detail
            </span>

            <span className="text-muted small">
              {prettyEnum(
                request.serviceCategory,
              )}{' '}
              /{' '}
              {prettyEnum(
                request.requestType,
              )}
            </span>

            <span
              className={`d-inline-flex align-items-center rounded px-2 py-1 border border-1 ${statusColor.bg ||
                ''
                } ${statusColor.text ||
                ''
                }`}
            >
              <span className="small">
                {prettyEnum(
                  request.status,
                )}
              </span>
            </span>

            <span className="text-muted small ms-md-2">
              Created:{' '}
              {request.createdAt
                ? baseFormatters.datetime.format(
                  new Date(
                    request.createdAt,
                  ),
                )
                : '—'}

              {' · '}

              Updated:{' '}
              {request.updatedAt
                ? baseFormatters.datetime.format(
                  new Date(
                    request.updatedAt,
                  ),
                )
                : '—'}
            </span>
          </div>
        </Col>

        <Col
          sm={4}
          className="text-end"
        >
          <span className="fw-semibold">
            <NavLink
              to={buildCompanyUrl(
                '/dashboard',
              )}
              className="block-link"
            >
              Dashboard
            </NavLink>

            {' - '}

            <NavLink
              to={buildCompanyUrl(
                '/requests',
              )}
              className="block-link"
            >
              Requests
            </NavLink>

            {' - Detail'}
          </span>
        </Col>
      </Row>

      {(errorMsg ||
        loading) && (
          <Row>
            <Col sm={12}>
              {errorMsg && (
                <div className="alert alert-danger">
                  {errorMsg}
                </div>
              )}

              {loading && (
                <div className="alert alert-info">
                  Loading...
                </div>
              )}
            </Col>
          </Row>
        )}

      <Row className="g-3">
        <Col
          lg={8}
          md={12}
        >
          <div
            style={{
              position:
                'sticky',
              top: 10,
            }}
          >
            <Card className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="fw-semibold">
                    Conversation
                  </div>

                  {request.preferredLanguage && (
                    <Badge
                      bg="light"
                      text="dark"
                      className="border"
                      title="Customer preferred language"
                    >
                      {request.preferredLanguage.toUpperCase()}
                    </Badge>
                  )}

                  <span className="text-muted small">
                    {messages.length}{' '}
                    message
                    {messages.length ===
                      1
                      ? ''
                      : 's'}
                  </span>
                </div>

                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={
                    refreshMessagesToBottom
                  }
                >
                  Refresh
                </Button>
              </div>

              {msgError && (
                <div className="alert alert-danger mb-2">
                  {msgError}
                </div>
              )}

              <div
                ref={listRef}
                style={{
                  maxHeight:
                    '45vh',
                  minHeight:
                    '320px',
                  overflowY:
                    'auto',
                  paddingRight:
                    4,
                }}
              >
                {msgLoading ? (
                  <div className="text-muted d-flex align-items-center gap-2">
                    <Spinner
                      size="sm"
                    />
                    Loading…
                  </div>
                ) : messages.length ===
                  0 ? (
                  <div className="text-muted">
                    No messages
                    yet.
                  </div>
                ) : (
                  messages.map(
                    (message) => (
                      <MessageBubble
                        key={
                          message.id
                        }
                        msg={
                          message
                        }
                        isAdmin={
                          isAdmin
                        }
                      />
                    ),
                  )
                )}
              </div>
            </Card>

            <ComposerRichText
              shops={shops}
              onSend={
                handleSend
              }
              isAdmin={
                isAdmin
              }
              initialHtml={
                composerInitialHtml
              }
              customerName={
                customerName
              }
              onGenerateMotoOffer={() =>
                setShowMotoSearch(
                  true,
                )
              }
              quoteLoading={
                quoteLoading
              }
            />
          </div>
        </Col>

        <Col
          lg={4}
          md={12}
        >
          <div
            style={{
              position:
                'sticky',
              top: 10,
              maxHeight:
                'calc(100vh - 20px)',
              overflowY:
                'auto',
              paddingRight:
                4,
            }}
          >
            {isAdmin && (
              <Card className="p-3 mb-3">
                <div className="fw-semibold mb-2">
                  Actions
                </div>

                <Form.Label className="fw-semibold mb-2">
                  Status
                </Form.Label>

                <Form.Select
                  value={
                    request.status ||
                    'OPEN'
                  }
                  onChange={(
                    event,
                  ) =>
                    handleStatusChange(
                      event.target
                        .value,
                    )
                  }
                >
                  <option value="OPEN">
                    Open
                  </option>

                  <option value="IN_PROGRESS">
                    In Progress
                  </option>

                  <option value="CLOSED">
                    Closed
                  </option>
                </Form.Select>
              </Card>
            )}

            {isAdmin && (
              <Card className="p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold">
                    Participating
                    shops
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    variant="outline-primary"
                    onClick={() =>
                      setShowAddShop(
                        true,
                      )
                    }
                  >
                    + Add
                  </Button>
                </div>

                {participantsError && (
                  <div className="alert alert-danger py-2">
                    {
                      participantsError
                    }
                  </div>
                )}

                {participantsLoading ? (
                  <div className="text-muted d-flex align-items-center gap-2">
                    <Spinner
                      size="sm"
                    />
                    Loading…
                  </div>
                ) : shops.length ===
                  0 ? (
                  <div className="text-muted small">
                    No shops linked
                    yet.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {shops.map(
                      (shop) => (
                        <div
                          key={
                            shop.participantId ||
                            shop.shopId
                          }
                          className="border rounded p-2"
                        >
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <div className="d-flex flex-column">
                              <div className="fw-semibold small">
                                {
                                  shop.name
                                }
                              </div>

                              <div className="text-muted small">
                                {shop.email ||
                                  '—'}
                              </div>
                            </div>

                            <Button
                              type="button"
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                handleRemoveShop(
                                  shop.participantId ||
                                  shop.shopId,
                                )
                              }
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}

                <div className="d-flex justify-content-end mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    onClick={
                      fetchParticipants
                    }
                  >
                    Refresh
                  </Button>
                </div>
              </Card>
            )}

            {isAdmin &&
              request.entityId && (
                <Card className="p-3 mb-3">
                  <div className="fw-semibold mb-2">
                    Booking
                  </div>

                  <div className="small">
                    <Badge
                      bg="light"
                      text="dark"
                      className="border"
                    >
                      #
                      {
                        request.entityId
                      }
                    </Badge>
                  </div>
                </Card>
              )}

            <Card className="p-3 mb-3">
              <div className="fw-semibold mb-2">
                Customer
              </div>

              <div className="small">
                <div className="fw-semibold">
                  {request.contactFirstName ||
                    '—'}{' '}
                  {request.contactLastName ||
                    ''}
                </div>

                <div className="text-muted">
                  Country:{' '}
                  {request.contactCountry ||
                    '—'}
                </div>

                <div className="text-muted">
                  Preferred
                  language:{' '}
                  {request.preferredLanguage
                    ? request.preferredLanguage.toUpperCase()
                    : '—'}
                </div>

                {isAdmin && (
                  <>
                    <div className="text-muted">
                      {request.contactEmail ||
                        '—'}
                    </div>

                    <div className="text-muted">
                      {request.contactPhoneCountry ||
                        ''}{' '}
                      {request.contactPhoneNumber ||
                        '—'}
                    </div>
                  </>
                )}
              </div>
            </Card>

            <Card className="p-3">
              <div className="fw-semibold mb-2">
                Request details
              </div>

              <div className="small d-flex flex-column gap-1">
                <div>
                  <span className="text-muted">
                    Location:
                  </span>{' '}
                  <span className="fw-semibold">
                    {request.place ||
                      '—'}
                  </span>
                </div>

                <div>
                  <span className="text-muted">
                    Dates:
                  </span>{' '}
                  {request.plannedFrom
                    ? new Date(
                      request.plannedFrom,
                    ).toLocaleDateString()
                    : '—'}

                  {' → '}

                  {request.plannedTo
                    ? new Date(
                      request.plannedTo,
                    ).toLocaleDateString()
                    : '—'}
                </div>

                <div>
                  <span className="text-muted">
                    People:
                  </span>{' '}
                  {typeof request.partySize ===
                    'number'
                    ? request.partySize
                    : '—'}
                </div>
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      {isAdmin && (
        <AddShopModal
          show={
            showAddShop
          }
          onClose={() =>
            setShowAddShop(
              false,
            )
          }
          onAdded={
            addShops
          }
          existingShopIds={
            existingShopIds
          }
        />
      )}

      {isAdmin && (
        <MotoSearchModal
          show={
            showMotoSearch
          }
          onClose={() =>
            setShowMotoSearch(
              false,
            )
          }
          onConfirm={
            handleGenerateMotoQuote
          }
          request={
            request
          }
        />
      )}
    </Container>
  );
};

export default RequestForm;